import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Grid,
  Link,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithCode, isAuthenticated, error, loading, clearError, checkAuth } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Handle OAuth callback with authorization code or token
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const token = urlParams.get('token');
    const error = urlParams.get('error');

    console.log('🔍 OAuth Callback Debug:', { 
      pathname: location.pathname, 
      search: location.search, 
      code, 
      token, 
      error,
      fullUrl: window.location.href 
    });

    if (error) {
      enqueueSnackbar(`Authentication error: ${error}`, { variant: 'error' });
      navigate('/login', { replace: true });
      return;
    }

    if (token) {
      console.log('🔑 Token found in URL, calling handleTokenCallback');
      handleTokenCallback(token);
    } else if (code) {
      console.log('🔐 Code found in URL, calling handleOAuthCallback');
      console.log(`🔐 Code value: ${code.substring(0, 20)}...`);
      handleOAuthCallback(code);
    } else {
      console.log('❌ No code or token found in URL');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleTokenCallback = async (token: string) => {
    try {
      // Store the token in cookies (AuthContext looks for 'auth_token' cookie)
      document.cookie = `auth_token=${token}; path=/; max-age=86400; secure; samesite=strict`;
      
      // The token is already in the cookie, just navigate to dashboard
      // AuthContext will automatically pick it up
      enqueueSnackbar('Successfully logged in!', { variant: 'success' });
      await checkAuth(); // Trigger auth check
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Token callback error:', error);
      enqueueSnackbar('Login failed. Please try again.', { variant: 'error' });
      navigate('/login', { replace: true });
    }
  };

  const handleOAuthCallback = async (code: string) => {
    try {
      console.log('🔐 handleOAuthCallback called with code');
      console.log(`🔐 Code length: ${code.length}`);
      console.log('🔐 Calling loginWithCode from AuthContext...');
      const result = await loginWithCode(code);
      console.log('✅ loginWithCode returned successfully');
      console.log('✅ Login result:', result);
      enqueueSnackbar('Successfully logged in!', { variant: 'success' });
    } catch (error: any) {
      console.error('❌ OAuth callback error:', error);
      console.error('❌ Error message:', error?.message);
      console.error('❌ Error stack:', error?.stack);
      enqueueSnackbar('Login failed. Please try again.', { variant: 'error' });
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    
    try {
      // Get the Google OAuth URL from backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/auth/google/url`);
      const data = await response.json();
      
      if (data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        throw new Error('No auth URL received');
      }
    } catch (error) {
      console.error('Error getting Google auth URL:', error);
      setIsGoogleLoading(false);
    }
  };

  const handleErrorClose = () => {
    clearError();
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress size={40} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
          2nd Opinion
        </Typography>
        <Typography variant="h2" component="h2" sx={{ mb: 3 }}>
          AI-based Analysis of Medical Reports
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 4, color: '#666' }}>
          Upload your medical report and receive AI-powered analysis via email
        </Typography>
      </Box>

      {/* Sign In Section - Centered */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" textAlign="center" sx={{ mb: 3 }}>
              Sign In
            </Typography>

            {error && (
              <Alert severity="error" onClose={handleErrorClose} sx={{ mb: 3 }}>
                {typeof error === 'string' ? error : JSON.stringify(error)}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={isGoogleLoading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading}
              sx={{
                mb: 3,
                py: 1.5,
                backgroundColor: '#4285f4',
                '&:hover': {
                  backgroundColor: '#357ae8',
                },
              }}
            >
              {isGoogleLoading ? 'Connecting...' : 'Continue with Google'}
            </Button>

            <Typography variant="body2" textAlign="center" sx={{ color: '#666', mb: 2 }}>
              Secure authentication powered by Google OAuth
            </Typography>

            <Box textAlign="center">
              <Link href="/privacy" sx={{ mr: 2, textDecoration: 'none' }}>
                Privacy Policy
              </Link>
              <Link href="/terms" sx={{ textDecoration: 'none' }}>
                Terms of Service
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* How it Works Section - Above Why Choose */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#2E86AB' }}>
          ℹ️ How it Works
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                1. Upload
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Select your medical report PDF file
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                2. Submit
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Click submit to securely upload your report
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                3. Process
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Our AI analyzes your report for abnormalities
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                4. Receive
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Get detailed analysis via email within minutes
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Why Choose 2nd Opinion Section - Below How it Works */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#2E86AB' }}>
          ⭐ Why Choose 2nd Opinion?
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                🔬 Advanced Analysis
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                AI-powered medical report analysis with detailed insights and recommendations
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                📧 Email Delivery
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Receive comprehensive analysis directly in your email inbox
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                🔒 Secure & Private
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Your medical data is encrypted and processed securely in the cloud
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Bottom Links */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          <Link 
            href="https://2ndopinionai.org/" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ color: '#2E86AB', textDecoration: 'none', mr: 3 }}
          >
            🏠 Home
          </Link>
          <Link href="/privacy" sx={{ color: '#2E86AB', textDecoration: 'none', mr: 3 }}>
            🔒 Privacy Policy
          </Link>
          <Link href="/terms" sx={{ color: '#2E86AB', textDecoration: 'none' }}>
            📜 Terms of Service
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default LoginPage;

// Trigger build - token callback fix deployed
